import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Facebook,
  Loader2,
  RefreshCw,
  Settings2,
  Users,
} from "lucide-react";
import { fetchClaw9Accounts } from "../api/claw9";
import { createMessengerBotConfig } from "../api/messengerBot";
import { completeOAuth, selectFacebookPage, subscribeFacebookPage } from "../api/socialMedia";
import { getChannelLabel } from "../utils/messagingChannels";

const DEFAULT_SETTINGS = {
  bot_name: "Mortgage Assistant",
  welcome_message: "Hi! How can I help today?",
  language: "en",
};

const normalizeAccounts = (result) => {
  const accounts = result?.accounts || result?.data?.accounts || result?.data || [];
  return Array.isArray(accounts) ? accounts : [];
};

const getFriendlyError = (message) => {
  const text = String(message || "").toLowerCase();

  if (text.includes("permission")) {
    return "Facebook permissions are missing. Reconnect Facebook and approve page and Messenger permissions.";
  }
  if (text.includes("webhook")) {
    return "Facebook connected, but Messenger webhook activation failed.";
  }
  if (text.includes("crm") && text.includes("account")) {
    return "No CRM account is available for Messenger.";
  }
  if (text.includes("auth")) {
    return "Facebook auth failed. Reconnect Facebook to continue Messenger setup.";
  }

  return message || "We connected Facebook, but couldn’t finish Messenger setup.";
};

const FlowSteps = ({ status }) => {
  const steps = [
    { id: "auth", label: "Connect Facebook" },
    { id: "page", label: "Choose Page" },
    { id: "crm", label: "Link CRM" },
    { id: "ready", label: "Ready" },
  ];

  const activeMap = {
    auth_in_progress: 0,
    page_selection: 1,
    crm_account_selection: 2,
    provisioning: 2,
    ready: 3,
    error: 1,
  };

  const activeIndex = activeMap[status] ?? 0;

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-xs font-medium text-white/60">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className={`px-3 py-1.5 rounded-full inline-flex items-center gap-2 ${
            index <= activeIndex ? "bg-[#ff4d4d]/15 text-white" : "bg-white/5 text-white/40"
          }`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              index <= activeIndex ? "bg-[#ff4d4d] text-white" : "bg-white/10"
            }`}>
              {index + 1}
            </span>
            {step.label}
          </div>
          {index < steps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-white/20" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const SocialOAuthCallback = () => {
  const [status, setStatus] = useState("auth_in_progress");
  const [pages, setPages] = useState([]);
  const [oauthState, setOauthState] = useState("");
  const [configPlatform, setConfigPlatform] = useState("messenger");
  const [integration, setIntegration] = useState(null);
  const [config, setConfig] = useState(null);
  const [crmAccounts, setCrmAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [loadingPageId, setLoadingPageId] = useState("");
  const [error, setError] = useState(null);

  const selectedAccount = useMemo(
    () => crmAccounts.find((account) => account.account_id === selectedAccountId) || null,
    [crmAccounts, selectedAccountId]
  );

  const finalizeSetup = useCallback(async (connectedIntegration, platform, crmAccountId = "") => {
    setStatus("provisioning");
    const payload = {
      integration_id: connectedIntegration.id,
      platform,
      ...DEFAULT_SETTINGS,
    };

    if (crmAccountId) {
      payload.crm_account_id = crmAccountId;
    }

    const created = await createMessengerBotConfig(payload);
    setConfig(created);
    sessionStorage.setItem("claw9_messaging_setup_success", "1");
    setStatus("ready");
  }, []);

  const resolveCrmAccounts = useCallback(async (connectedIntegration, platform) => {
    const result = await fetchClaw9Accounts();
    const accounts = normalizeAccounts(result).filter((account) => account.status !== "inactive");
    setCrmAccounts(accounts);

    if (accounts.length === 0) {
      throw new Error("No CRM account available");
    }

    if (accounts.length === 1) {
      setSelectedAccountId(accounts[0].account_id);
      await finalizeSetup(connectedIntegration, platform);
      return;
    }

    setStatus("crm_account_selection");
  }, [finalizeSetup]);

  useEffect(() => {
    const run = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");
        const flowType = sessionStorage.getItem("claw9_messaging_flow");
        const messagingChannel = sessionStorage.getItem("claw9_messaging_channel") || "messenger";
        const socialPlatform = sessionStorage.getItem("claw9_oauth_platform") || "facebook";

        if (flowType !== "connected-messaging") {
          throw new Error("Messaging setup session not found.");
        }

        setConfigPlatform(messagingChannel);

        if (errorParam) {
          throw new Error(searchParams.get("error_description") || "Facebook auth failed");
        }

        if (!code || !state) {
          throw new Error("No OAuth code received from Facebook.");
        }

        const response = await completeOAuth({ platform: socialPlatform, code, state });
        if (response.requiresSelection === true || response.requires_selection === true) {
          setPages(response.pages || []);
          setOauthState(response.oauthState || response.oauth_state || "");
          setStatus("page_selection");
          return;
        }

        if (!response?.id) {
          throw new Error(`OAuth completed, but ${getChannelLabel(messagingChannel)} integration was not returned.`);
        }

        setIntegration(response);
        await resolveCrmAccounts(response, messagingChannel);
      } catch (callbackError) {
        setError(getFriendlyError(callbackError.message));
        setStatus("error");
      }
    };

    run();
  }, [resolveCrmAccounts]);

  const handlePageSelect = async (page) => {
    try {
      setLoadingPageId(page.id);
      const connectedIntegration = await selectFacebookPage({
        oauthState,
        pageId: page.id,
        pageName: page.name,
        pageAccessToken: page.access_token,
      });
      setIntegration(connectedIntegration);

      // Subscribe page to Claw9 webhook to receive messages
      const brandId = sessionStorage.getItem("claw9_social_brand_id") || window.localStorage.getItem("claw9_social_brand_id");
      if (brandId && page.id) {
        try {
          await subscribeFacebookPage({ accountId: brandId, pageId: page.id });
        } catch (subscribeError) {
          console.warn("[OAuth] Page connected but webhook subscription failed:", subscribeError.message);
          // Continue anyway - the page is connected, just webhook might need manual setup
        }
      }

      await resolveCrmAccounts(connectedIntegration, configPlatform);
    } catch (selectionError) {
      setError(getFriendlyError(selectionError.message));
      setStatus("error");
    } finally {
      setLoadingPageId("");
    }
  };

  const handleContinue = async () => {
    if (!integration || !selectedAccountId) return;

    try {
      await finalizeSetup(integration, configPlatform, selectedAccountId);
    } catch (setupError) {
      setError(getFriendlyError(setupError.message));
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="mb-6">
          <FlowSteps status={status} />
        </div>

        <div className="bg-[#0d1424] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          <div className="px-8 py-6 border-b border-white/10 bg-gradient-to-r from-[#0f172a] to-[#111827]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1877f2]/15 text-[#4d94ff] flex items-center justify-center">
                <Facebook className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-white/50">Claw9 Messenger setup</p>
                <h1 className="text-2xl font-semibold">Connect {getChannelLabel(configPlatform)}</h1>
              </div>
            </div>
          </div>

          <div className="p-8">
            {status === "auth_in_progress" && (
              <div className="text-center py-8">
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#ff4d4d] animate-spin" />
                <h2 className="text-2xl font-semibold mb-2">Completing channel sign-in</h2>
                <p className="text-white/60">We’re loading the connected source needed for {getChannelLabel(configPlatform)}.</p>
              </div>
            )}

            {status === "page_selection" && (
              <div>
                <div className="text-center mb-8">
                  <Facebook className="w-16 h-16 mx-auto mb-5 text-[#4d94ff]" />
                  <h2 className="text-2xl font-semibold mb-2">Choose a source account</h2>
                  <p className="text-white/60">Claw9 will provision the CRM inbox sync automatically for {getChannelLabel(configPlatform)}.</p>
                </div>

                <div className="grid gap-3 max-w-2xl mx-auto">
                  {pages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => handlePageSelect(page)}
                      disabled={Boolean(loadingPageId)}
                      className={`p-4 rounded-2xl border text-left transition-colors ${
                        loadingPageId === page.id
                          ? "border-[#4d94ff]/40 bg-[#1877f2]/10"
                          : "border-white/10 bg-[#050810] hover:border-white/20 hover:bg-white/[0.03]"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {page.pictureUrl ? (
                          <img src={page.pictureUrl} alt={page.name} className="w-12 h-12 rounded-full object-cover border border-white/10" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#1877f2]/15 text-[#4d94ff] flex items-center justify-center">
                            <Facebook className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{page.name}</p>
                          <p className="text-sm text-white/50 truncate">
                            {page.category || "Facebook Page"}
                            {typeof page.fanCount === "number" ? ` • ${page.fanCount.toLocaleString()} followers` : ""}
                          </p>
                        </div>
                        {loadingPageId === page.id ? (
                          <Loader2 className="w-5 h-5 animate-spin text-[#ff4d4d]" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-white/40" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {status === "crm_account_selection" && (
              <div className="max-w-lg mx-auto">
                <div className="text-center mb-6">
                  <Users className="w-14 h-14 mx-auto mb-4 text-white/70" />
                  <h2 className="text-2xl font-semibold mb-2">Choose CRM Account</h2>
                  <p className="text-white/60">{getChannelLabel(configPlatform)} conversations will appear in this inbox.</p>
                </div>
                <select
                  value={selectedAccountId}
                  onChange={(event) => setSelectedAccountId(event.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-[#050810] border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
                >
                  <option value="">Select an inbox</option>
                  {crmAccounts.map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.display_name || account.name || account.account_id}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleContinue}
                  disabled={!selectedAccountId}
                  className="mt-4 w-full px-4 py-3 rounded-2xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white font-medium transition-colors disabled:opacity-60"
                >
                  Continue
                </button>
              </div>
            )}

            {status === "provisioning" && (
              <div className="text-center py-8">
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#ff4d4d] animate-spin" />
                <h2 className="text-2xl font-semibold mb-2">Provisioning Inbox Channel</h2>
                <p className="text-white/60">Creating the channel config and linking CRM automatically for {getChannelLabel(configPlatform)}.</p>
              </div>
            )}

            {status === "ready" && (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-6 text-emerald-400" />
                  <h2 className="text-2xl font-semibold mb-2">{getChannelLabel(config?.platform || configPlatform)} is live</h2>
                  <p className="text-white/60">New channel messages will now appear in your Claw9 inbox.</p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 mb-8">
                  <div className="p-4 rounded-2xl bg-[#050810] border border-white/10">
                    <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Connected source</p>
                    <p className="font-medium text-white">{config?.page_name || integration?.accountName || "Connected account"}</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#050810] border border-white/10">
                    <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Channel status</p>
                    <p className="font-medium text-emerald-300">{getChannelLabel(config?.platform || configPlatform)} live</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#050810] border border-white/10">
                    <p className="text-xs text-white/40 uppercase tracking-wide mb-1">CRM account</p>
                    <p className="font-medium text-white">
                      {selectedAccount?.display_name || selectedAccount?.name || config?.crm_account_id || "Auto-linked"}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#050810] border border-white/10">
                    <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Last sync status</p>
                    <p className="font-medium text-white">Active now</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`/?tab=claw9-chat&channel=${encodeURIComponent(config?.platform || configPlatform)}`}
                    className="flex-1 px-5 py-3 rounded-2xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white font-medium text-center transition-colors"
                  >
                    Open Inbox
                  </a>
                  <a
                    href="/?tab=messaging-channels"
                    className="flex-1 px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-medium text-center transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Settings2 className="w-4 h-4" />
                    Manage Settings
                  </a>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="max-w-xl mx-auto text-center">
                <AlertTriangle className="w-16 h-16 mx-auto mb-6 text-amber-400" />
                <h2 className="text-2xl font-semibold mb-2">Channel setup incomplete</h2>
                <p className="text-white/60 mb-6">{error}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <a
                    href="/?tab=messaging-channels"
                    className="px-5 py-3 rounded-2xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white font-medium text-center transition-colors"
                  >
                    Try Again
                  </a>
                  <a
                    href="mailto:support@claw9.ai"
                    className="px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-medium text-center transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Contact Support
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialOAuthCallback;
